import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { STATUS_API } from 'src/app/constant/config';
import { getHereAddresses, getHereStatus } from 'src/features/hereSlice';

const PAGE_SIZE = 20;

/**
 * Khối "Dữ liệu bản đồ đã thu thập".
 *
 * CHỈ TRA CỨU — không có nút chạy/dừng thu thập, vì việc đó tiêu quota HERE và chạy nền.
 * Mỗi dòng hiển thị hai lớp: chữ THÔ của HERE (chưa cập nhật sáp nhập 01/7/2025) và đơn vị
 * hành chính MỚI đã quy đổi, để người vận hành đối chiếu được vì sao ra kết quả đó.
 */
const HereDataPanel = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const status = useSelector(state => state.hereSlice.status);
  const addresses = useSelector(state => state.hereSlice.addresses);
  const meta = useSelector(state => state.hereSlice.addressesMeta);
  const statusGetStatus = useSelector(state => state.hereSlice.statusGetStatus);
  const statusGetAddresses = useSelector(state => state.hereSlice.statusGetAddresses);
  const error = useSelector(state => state.hereSlice.error);

  const [filters, setFilters] = useState({ province: '', street: '', q: '' });
  const [page, setPage] = useState(0);

  const load = useCallback(
    (nextPage = page) => {
      dispatch(getHereAddresses({ ...filters, page: nextPage + 1, limit: PAGE_SIZE }));
    },
    [dispatch, filters, page]
  );

  useEffect(() => {
    dispatch(getHereStatus());
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = () => {
    setPage(0);
    load(0);
  };

  const changePage = (event, nextPage) => {
    setPage(nextPage);
    load(nextPage);
  };

  const byStatus = status?.roads?.byStatus ?? {};

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h5">Dữ liệu bản đồ đã thu thập</Typography>
        <Typography variant="body2" color="textSecondary">
          Địa chỉ lấy từ HERE dọc các tuyến đường Việt Nam, lưu trong cơ sở dữ liệu riêng của dịch vụ bản đồ.
        </Typography>

        {statusGetStatus === STATUS_API.FAILURE && (
          <Typography className={classes.error} variant="body2">
            Không đọc được dịch vụ bản đồ: {error}
          </Typography>
        )}

        <Divider className={classes.divider} />

        <Box display="flex" flexWrap="wrap" gridGap={32}>
          <Metric label="Địa chỉ đã thu" value={formatNumber(status?.addresses)} />
          <Metric label="Tuyến đã quét xong" value={formatNumber(byStatus.done)} />
          <Metric label="Tuyến còn lại" value={formatNumber((byStatus.pending ?? 0) + (byStatus.queued ?? 0))} />
          <Metric label="Tuyến lỗi" value={formatNumber(byStatus.failed)} highlight={(byStatus.failed ?? 0) > 0} />
        </Box>

        <Divider className={classes.divider} />

        <Box display="flex" flexWrap="wrap" gridGap={8} alignItems="center">
          <TextField
            size="small"
            variant="outlined"
            label="Tỉnh/thành"
            value={filters.province}
            onChange={event => setFilters({ ...filters, province: event.target.value })}
          />
          <TextField
            size="small"
            variant="outlined"
            label="Tên đường"
            value={filters.street}
            onChange={event => setFilters({ ...filters, street: event.target.value })}
          />
          <TextField
            size="small"
            variant="outlined"
            label="Từ khóa"
            placeholder="Số nhà, tên địa điểm"
            value={filters.q}
            onChange={event => setFilters({ ...filters, q: event.target.value })}
            onKeyPress={event => event.key === 'Enter' && search()}
          />
          <Button variant="contained" color="primary" onClick={search}>
            Tìm
          </Button>
        </Box>

        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Địa chỉ</TableCell>
              <TableCell>Đường</TableCell>
              <TableCell>Đơn vị hành chính (HERE)</TableCell>
              <TableCell>Sau sáp nhập 01/7/2025</TableCell>
              <TableCell align="right">Tọa độ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addresses.map(item => (
              <TableRow key={item.id} hover>
                <TableCell>
                  {item.title}
                  {item.house_number && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      Số nhà {item.house_number}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{item.street || '—'}</TableCell>
                <TableCell>{[item.ward, item.district, item.province].filter(Boolean).join(', ') || '—'}</TableCell>
                <TableCell>
                  {item.ward_2025 || item.province_2025 ? (
                    <>
                      {[item.ward_2025, item.province_2025].filter(Boolean).join(', ')}
                      <Typography variant="caption" display="block" color="textSecondary">
                        {MATCH_LABEL[item.resolve_match] || item.resolve_match}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      Chưa tra được
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {Number(item.lat).toFixed(5)}, {Number(item.lon).toFixed(5)}
                </TableCell>
              </TableRow>
            ))}
            {!addresses.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {statusGetAddresses === STATUS_API.PENDING ? 'Đang tải...' : 'Chưa có dữ liệu'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={meta?.total ?? 0}
          page={page}
          onChangePage={changePage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} trên ${count}`}
        />
      </CardContent>
    </Card>
  );
};

/** Nhãn cho độ chắc chắn của phép quy đổi đơn vị hành chính. */
const MATCH_LABEL = {
  exact: 'Khớp chính xác',
  partial: 'Đơn vị cũ bị chia — cần kiểm lại',
  ambiguous: 'Nhiều khả năng — chưa chốt',
  already_current: 'Đã là tên mới'
};

const formatNumber = value => (value ?? 0).toLocaleString('vi-VN');

const Metric = ({ label, value, highlight }) => {
  const classes = useStyles();
  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="h4" className={highlight ? classes.error : undefined}>
        {value}
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  card: { marginTop: theme.spacing(2) },
  divider: { margin: `${theme.spacing(2)}px 0` },
  table: { marginTop: theme.spacing(2) },
  error: { color: theme.palette.error.main }
}));

export default HereDataPanel;
